import {
	APPLICANT_TYPE_OPTION,
	CaroEnum,
	CELL_PROGRAM_NAME_ASSIGNMENT_HEADER,
	CELL_PROGRAM_NAME_ASSIGNMENT_HEADER_DATA,
	CELL_PROGRAM_NAME_ASSIGNMENT_REASON_FAIL,
	COLUMN_START,
	COLUMN_START_CUSTOM_FIELD_APPLICANT,
	COLUMN_START_CUSTOM_FIELD_FRANCHISE,
	EXCEL_RESULT_MESSAGES,
	ExtensionStayEnum,
	ExtensionStayLabel,
	FILE_TYPE_EXPORT_EXCEL,
	GenderEnum,
	GradeEnum,
	NATIONAL_LIST_WITH_EN,
	OPTIONAL_HEADERS_APPLICANT,
	OPTIONAL_HEADERS_ASSIGNMENT,
	OPTIONAL_HEADERS_FRANCHISE,
	OPTIONAL_HEADERS_LODGING,
	OPTIONAL_HEADERS_PUBLIC_ASSIGNMENT,
	OPTIONAL_HEADERS_RECOMMENDER,
	PageTypeEnum,
	ResidenceEnum,
	ROW_HEADER_APPLICANT,
	ROW_HEADER_ASSIGNMENT,
	ROW_HEADER_FRANCHISE,
	ROW_HEADER_LODGING,
	ROW_HEADER_PUBLIC_ASSIGNMENT,
	ROW_HEADER_RECOMMENDER,
	TEMPLATE_APPLICANT_FIELDS,
	TEMPLATE_ASSIGNMENT_FIELDS,
	TEMPLATE_FRANCHISE_FIELDS,
	TEMPLATE_LODGING_FIELDS,
	TEMPLATE_PUBLIC_ASSIGNMENT_FIELDS,
	TEMPLATE_RECOMMENDER_FIELDS,
	TUploadExcelPage,
	TypeTemplateExcelEnum,
	VisaTimeEnum,
	VisaTimeLabel,
} from '@/constant';
import {
	IApplicantListItem,
	IFranchiseListItem,
	TExportExcelRecommenderResponse,
	TUploadExcelApplicantItem,
	TUploadExcelAssignmentItem,
	TUploadExcelCurrentAssignmentItem,
	TUploadExcelFranchiseItem,
	TUploadExcelLodgingItem,
	TUploadExcelPublicAssignmentItem,
	TUploadExcelRecommenderItem,
	WorkerLodgingListItem,
} from '@/services';
import {
	IWorkerAssignmentUploadAssignmentFailedDetailStore,
	IWorkerAssignmentUploadPublicAssignmentFailedDetailStore,
	TApplicantUploadFailedDetailStore,
	TFranchiseUploadFailedDetailStore,
	TUnitSettingStore,
	TUploadExcelLodgingFailedItemStore,
} from '@/stores';
import { TRecommenderUploadFailedDetailStore } from '@/stores/worker-recommender';

import { DefaultOptionType } from 'antd/es/select';

import ExcelJS, { RowMap, Worksheet } from 'exceljs';
import { saveAs } from 'file-saver';
import { TFunction } from 'i18next';
import { clone, includes } from 'ramda';
import { isNilOrEmpty } from 'ramda-adjunct';

import { showAlert } from './alert.util';
import {
	convertFranchiseTypeCodeToName,
	convertFranchiseTypeDetailCodeToName,
	convertGenderCodeToName,
	convertGradeCodeToName,
	convertInfrastructureCodeToName,
	convertLodgingProviderTypeCodeToName,
	convertNationCodeToName,
	convertOwnershipCodeToName,
	convertProductionTypeCodeToName,
	insertObjectIf,
	map,
} from './common.util';
import {
	dayjs,
	formatDate,
	getDiffExpirationDate,
	SUPPORT_FORMAT_DATE_LIST,
} from './date.util';
import {
	applyCellStyle,
	columnToNumber,
	getRecommenderTypeLabel,
} from './excel.util';

export const getRowHeader = (pageType: TUploadExcelPage) => {
	switch (pageType) {
		case PageTypeEnum.APPLICANT:
			return ROW_HEADER_APPLICANT;
		case PageTypeEnum.FRANCHISE:
			return ROW_HEADER_FRANCHISE;
		case PageTypeEnum.RECOMMENDER:
			return ROW_HEADER_RECOMMENDER;
		case PageTypeEnum.ASSIGNMENT:
			return ROW_HEADER_ASSIGNMENT;
		case PageTypeEnum.LODGING:
			return ROW_HEADER_LODGING;
		case PageTypeEnum.PUBLIC_ASSIGNMENT:
			return ROW_HEADER_PUBLIC_ASSIGNMENT;
	}
};

export const getProgramNameOfAssignment = (worksheet: Worksheet) => {
	const cell = worksheet.getCell(CELL_PROGRAM_NAME_ASSIGNMENT_HEADER);
	const cellText = cell.text?.trim() || cell.value?.toString().trim() || '';
	const cleanedText = cellText.replace(/\([^)]*\)/g, '').trim();
	return cleanedText;
};

export const getHeaderOfExcel = ({
	worksheet,
	rowHeader,
	pageType,
}: {
	worksheet: Worksheet;
	rowHeader: number[];
	pageType?: TUploadExcelPage;
}) => {
	// Extract headers from the specified rows
	const headers: string[] = [];
	const headerValues: string[][] = rowHeader.map((rowNumber) => {
		const row = worksheet.getRow(rowNumber);
		const values: string[] = [];
		row.eachCell({ includeEmpty: false }, (cell) => {
			if (cell) {
				try {
					let cellText =
						cell.text?.trim() || cell.value?.toString().trim() || '';

					if (cellText === '[object Object]') {
						cellText = map(
							(cell as any)?.value?.richText,
							(item: any) => item?.text
						).join('');
					}

					values.push(cellText);
				} catch (error) {
					console.error('Error processing cell:', cell, error);
				}
			} else {
				console.error('Encountered a null or undefined cell:', cell);
			}
		});
		return values;
	});
	// Combine headers across specified rows
	const numColumns = Math.max(...headerValues.map((row) => row.length));
	for (let col = 1; col <= numColumns; col++) {
		const headerParts = headerValues.map((row) => row[col - 1] || '');
		let combinedHeader = headerParts.join('_').replace(/\s+/g, ' ');

		// Remove redundancy in the combined header
		combinedHeader = combinedHeader
			.split('_')
			.filter((item, index, self) => self.indexOf(item) === index)
			.join('_');

		// Remove text within parentheses including the parentheses
		const cleanedHeader = combinedHeader.replace(/\([^)]*\)/g, '').trim();

		headers.push(cleanedHeader);
	}

	if (
		[
			PageTypeEnum.ASSIGNMENT,
			// PageTypeEnum.PUBLIC_ASSIGNMENT,
		].includes(pageType as PageTypeEnum)
	) {
		const programName = getProgramNameOfAssignment(worksheet);
		headers.unshift(programName);
	}

	return headers;
};

export const getDateProgramNameOfAssignment = ({
	worksheet,
	header,
}: {
	worksheet: Worksheet;
	header: string;
}) => {
	const cell = worksheet.getCell(CELL_PROGRAM_NAME_ASSIGNMENT_HEADER_DATA);
	const cellText = cell.text?.trim();

	return { [header]: cellText };
};

export const formatTextByPattern = (value: string, pattern: string): string => {
	if (!value || !pattern || pattern === '@') return value; // @ is the default format code for text in Excel,format text but enter a number

	if (!/^\d+$/.test(value)) {
		return value; // return original value if value contains non-numeric characters
	}

	let result: string[] = [];
	let valueIndex = value.length - 1; // start from the end of the value string
	const patternReversed = pattern.split('').reverse(); // reverse pattern

	// apply pattern from right to left
	for (const char of patternReversed) {
		if (char === '0' || char === '#') {
			if (valueIndex >= 0) {
				result.push(value[valueIndex]);
				valueIndex--;
			} else {
				result.push(char === '0' || char === '#' ? '0' : ''); // add '0' if char is '0' or '#' if value is not enough to fill pattern
			}
		} else {
			result.push(char); // keep original character that is not '0' or '#'
		}
	}

	// add remaining value (if any) to the beginning
	if (valueIndex >= 0) {
		result.push(value.slice(0, valueIndex + 1));
	}

	// reverse result and return
	return result.reverse().join('');
};

export const getDataOfExcel = ({
	worksheet,
	rowHeader,
	headers,
	pageType,
}: {
	worksheet: Worksheet;
	rowHeader: number[];
	headers: string[];
	pageType?: TUploadExcelPage;
}) => {
	// Extract data starting from the row after the last header row
	const dataStartRow = Math.max(...rowHeader) + 1;
	const data: RowMap[] = [];

	let programNameHeader = '';
	let programNameData: { [key: string]: string } = {};
	let remainingHeaders: string[] = clone(headers);
	if (
		includes(pageType, [
			PageTypeEnum.ASSIGNMENT,
			// PageTypeEnum.PUBLIC_ASSIGNMENT,
		])
	) {
		remainingHeaders = headers.slice(1);
		programNameHeader = headers[0] || '';
		programNameData = getDateProgramNameOfAssignment({
			worksheet,
			header: programNameHeader,
		});
	}

	worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
		const values = (row?.values as any)?.filter((v: any) => {
			if (v === undefined || v === null) return false;
			if (typeof v === 'string' && v.trim() === '') return false;
			return true;
		});

		if (values?.length === 0) {
			return;
		}

		if (rowNumber >= dataStartRow) {
			let rowData: RowMap = {};
			let colIndex = 0;
			row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
				if (
					colNumber < COLUMN_START ||
					isNilOrEmpty(remainingHeaders[colIndex])
				) {
					return;
				}
				if (cell.type === ExcelJS.ValueType.Date) {
					rowData[remainingHeaders[colIndex]] = formatDate(cell?.text, {
						outputFormat: 'YYYY-MM-DD',
					});
				} else if (cell.type === ExcelJS.ValueType.Number) {
					rowData[remainingHeaders[colIndex]] = cell?.value;
				} else if (cell.numFmt) {
					// Format number by pattern defined in numFmt
					rowData[remainingHeaders[colIndex]] = formatTextByPattern(
						cell?.text?.trim()?.replace(/\n/g, ' '),
						cell.numFmt
					);
				} else {
					rowData[remainingHeaders[colIndex]] = cell?.text
						?.trim()
						?.replace(/\n/g, ' ');
				}

				colIndex++;
			});
			rowData = { ...rowData, ...programNameData };
			data.push(rowData);
		}
	});

	return data;
};

export const getNameOfTemplateFile = ({
	pageType,
	type,
}: {
	pageType: TUploadExcelPage;
	type: TypeTemplateExcelEnum;
}) => {
	if (type === TypeTemplateExcelEnum.TEMPLATE) {
		switch (pageType) {
			case PageTypeEnum.APPLICANT:
				return 'template_applicant_v4.0';
			case PageTypeEnum.FRANCHISE:
				return 'template_franchise_v4.0';
			case PageTypeEnum.RECOMMENDER:
				return 'template_recommender_v4.0';
			case PageTypeEnum.ASSIGNMENT:
				return 'template_assignment_v4.0';
			case PageTypeEnum.LODGING:
				return 'template_lodging_v4.0';
			case PageTypeEnum.PUBLIC_ASSIGNMENT:
				return 'template_public_assignment_v4.0';
		}
	} else if (type === TypeTemplateExcelEnum.FAILED_DATA) {
		switch (pageType) {
			case PageTypeEnum.APPLICANT:
				return 'template_applicant_failed_v4.0';
			case PageTypeEnum.RECOMMENDER:
				return 'template_recommender_failed_v4.0';
			case PageTypeEnum.LODGING:
				return 'template_lodging_failed_v4.0';
			case PageTypeEnum.FRANCHISE:
				return 'template_franchise_failed_v4.0';
			case PageTypeEnum.ASSIGNMENT:
				return 'template_assignment_failed_v4.0';
			case PageTypeEnum.PUBLIC_ASSIGNMENT:
				return 'template_public_assignment_failed_v4.0';
			default:
				return '';
		}
	} else return '';
};

export const setNameOfFileWhenDownload = ({
	pageType,
	type,
}: {
	pageType: TUploadExcelPage;
	type: TypeTemplateExcelEnum;
}) => {
	const today = formatDate(new Date(), { outputFormat: 'YYYYMMDD' });
	const hour = formatDate(new Date(), { outputFormat: 'HH' });
	const minute = formatDate(new Date(), { outputFormat: 'mm' });

	if (type === TypeTemplateExcelEnum.TEMPLATE) {
		switch (pageType) {
			case PageTypeEnum.APPLICANT:
				return `엑셀양식_근로자.${today}`;
			case PageTypeEnum.FRANCHISE:
				return `엑셀양식_사업장.${today}`;
			case PageTypeEnum.RECOMMENDER:
				return '추천자 추가 엑셀 양식';
			case PageTypeEnum.ASSIGNMENT:
				return `엑셀양식_일반_배정_${today}${hour}${minute}`;
			case PageTypeEnum.LODGING:
				return '숙소 추가 엑셀 양식';
			case PageTypeEnum.PUBLIC_ASSIGNMENT:
				return `엑셀양식_공공형_배정_${today}${hour}${minute}`;
		}
	} else if (type === TypeTemplateExcelEnum.CURRENT_DATA) {
		switch (pageType) {
			case PageTypeEnum.APPLICANT:
				return `엑셀목록_근로자_${today}${hour}${minute}`;
			case PageTypeEnum.FRANCHISE:
				return `엑셀목록_사업장_${today}${hour}${minute}`;
			case PageTypeEnum.RECOMMENDER:
				return `추천자 목록 ${today}`;
			case PageTypeEnum.ASSIGNMENT:
				return `엑셀목록_배정_${today}${hour}${minute}`;
			case PageTypeEnum.LODGING:
				return `숙소 목록 ${today}`;
			case PageTypeEnum.PUBLIC_ASSIGNMENT:
				return `엑셀목록_공공형배정_${today}${hour}${minute}`;
		}
	} else if (type === TypeTemplateExcelEnum.FAILED_DATA) {
		switch (pageType) {
			case PageTypeEnum.APPLICANT:
				return `근로자 미등록 목록_${today}${hour}${minute}`;
			case PageTypeEnum.FRANCHISE:
				return `사업장 미등록 목록_${today}${hour}${minute}`;
			case PageTypeEnum.RECOMMENDER:
				return `추천자 미등록 목록 ${today}${hour}${minute}`;
			case PageTypeEnum.ASSIGNMENT:
				return `엑셀양식_일반_배정_${today}${hour}${minute}`;
			case PageTypeEnum.LODGING:
				return `숙소 미등록 목록 ${today}${hour}${minute}`;
			case PageTypeEnum.PUBLIC_ASSIGNMENT:
				return `엑셀양식_공공형_배정_${today}${hour}${minute}`;
		}
	}
};

export const readFileExcel = ({
	file,
	rowHeader,
	pageType,
}: {
	file: File;
	rowHeader: number[];
	pageType?: TUploadExcelPage;
}): Promise<{ headers: string[]; data: RowMap[] }> => {
	return new Promise((resolve, reject) => {
		let headers: string[] = [];
		let data: RowMap[] = [];
		if (!file) return resolve({ headers, data });

		const reader = new FileReader();

		reader.onload = async (event) => {
			if (
				event.target == null ||
				event.target.result == null ||
				typeof event.target.result === 'string'
			) {
				return resolve({ headers, data });
			}

			const result = event.target.result as ArrayBuffer;
			const workbook = new ExcelJS.Workbook();

			try {
				await workbook.xlsx.load(result);
				const worksheet = workbook.worksheets[0];

				// Extract headers from the specified rows
				headers = getHeaderOfExcel({
					worksheet,
					rowHeader: rowHeader,
					pageType,
				});

				// Extract data starting from the row after the last header row
				data = getDataOfExcel({
					worksheet,
					rowHeader: rowHeader,
					headers,
					pageType,
				});

				resolve({ headers, data });
			} catch (error) {
				console.error(error);
				reject(error);
			}
		};

		reader.onerror = (error) => {
			showAlert('변경이 생겼습니다. 신규 파일을 다시 업로드해주세요.');
			reject(error);
		};

		reader.readAsArrayBuffer(file);
	});
};

export const getTemplate = async (
	pageType: TUploadExcelPage,
	customFields?: string[],
	headerRows: number[] = []
) => {
	try {
		// read file excel from public folder
		const templateName = getNameOfTemplateFile({
			pageType,
			type: TypeTemplateExcelEnum.TEMPLATE,
		});

		const template = await fetch(
			`${process.env.PUBLIC_URL}/template-excel/${templateName}.xlsx`
		);

		if (!template.ok) {
			throw new Error('Failed to fetch the template');
		}

		const arrayBuffer = await template.arrayBuffer();
		const workbook = new ExcelJS.Workbook();
		await workbook.xlsx.load(arrayBuffer);

		const worksheet = workbook.getWorksheet(1);
		const headerRow = Math.max(...headerRows);

		//fill header custom field to template
		if (
			pageType === PageTypeEnum.APPLICANT ||
			pageType === PageTypeEnum.FRANCHISE
		) {
			const startColumnCustomField =
				pageType === PageTypeEnum.APPLICANT
					? columnToNumber(COLUMN_START_CUSTOM_FIELD_APPLICANT)
					: columnToNumber(COLUMN_START_CUSTOM_FIELD_FRANCHISE);

			customFields?.forEach((field, index) => {
				const row = worksheet?.getRow(headerRow);
				if (row) {
					const cell = row.getCell(startColumnCustomField + index);
					if (cell) {
						cell.value = field;
					}
				}
			});
		}

		const bufferOutput = await workbook.xlsx.writeBuffer();
		const blob = new Blob([bufferOutput], {
			type: FILE_TYPE_EXPORT_EXCEL,
		});

		return blob;
	} catch (error) {
		console.error(error);
	}
};

export const getHeaderFromTemplateFile = async (pageType: TUploadExcelPage) => {
	try {
		const blob = await getTemplate(pageType);
		if (!blob) return [];
		const arrayBuffer = await blob.arrayBuffer();

		const workbook = new ExcelJS.Workbook();
		await workbook.xlsx.load(arrayBuffer);

		const worksheet = workbook.worksheets[0];
		const rowHeader = getRowHeader(pageType);

		const headers = getHeaderOfExcel({ worksheet, rowHeader, pageType });
		return headers;
	} catch (error) {
		console.error(error);
		throw error;
	}
};

export const validateHeaders = ({
	headers,
	headersTemplate,
	optionalHeaders,
}: {
	headers: string[];
	headersTemplate: string[];
	optionalHeaders?: string[];
}): boolean => {
	// Check if headers have all the fields in headersTemplate
	for (const header of headersTemplate) {
		if (!headers.includes(header)) {
			return false;
		}
	}

	// Check if headers contains elements other than headersTemplate and optionalHeaders
	for (const header of headers) {
		if (
			!headersTemplate.includes(header) &&
			!optionalHeaders?.includes(header)
		) {
			return false;
		}
	}

	return true;
};

export const getOptionalHeaders = (pageType: TUploadExcelPage) => {
	let headers: string[] = [];

	switch (pageType) {
		case PageTypeEnum.APPLICANT:
			headers = OPTIONAL_HEADERS_APPLICANT;
			break;
		case PageTypeEnum.FRANCHISE:
			headers = OPTIONAL_HEADERS_FRANCHISE;
			break;
		case PageTypeEnum.RECOMMENDER:
			headers = OPTIONAL_HEADERS_RECOMMENDER;
			break;
		case PageTypeEnum.ASSIGNMENT:
			headers = OPTIONAL_HEADERS_ASSIGNMENT;
			break;
		case PageTypeEnum.LODGING:
			headers = OPTIONAL_HEADERS_LODGING;
			break;
		case PageTypeEnum.PUBLIC_ASSIGNMENT:
			headers = OPTIONAL_HEADERS_PUBLIC_ASSIGNMENT;
			break;
	}

	return headers;
};

export const removeSpace = (str: string = '') => {
	return str?.replace(/\s+/g, '');
};

export const replaceDistrictName = (
	name: string,
	districtListOption: DefaultOptionType[] = []
) => {
	const _name = removeSpace(name);
	return (
		districtListOption.find(
			(option) => removeSpace(option.label as string) === _name
		)?.value || name
	);
};

export const replaceGender = (gender: string) => {
	switch (gender) {
		case 'F':
		case '여':
		case '여자':
		case '여성':
			return GenderEnum.FEMALE;
		case 'M':
		case '남':
		case '남자':
		case '남성':
			return GenderEnum.MALE;
		default:
			return gender;
	}
};

export const replaceNational = (national: string) => {
	const _national = removeSpace(national);
	return (
		NATIONAL_LIST_WITH_EN.find(
			(item) => item.label === _national || item.labelEnglish === _national
		)?.value || national
	);
};

const convertApplicantTypeCodeToName = (applicantType: string): string => {
	const options = APPLICANT_TYPE_OPTION.filter((e) => e?.value !== '');
	const found = options.find((e) => e?.value === applicantType);
	return found ? found?.label : applicantType;
};

export const replaceGrade = (grade: string) => {
	const _grade = removeSpace(grade);

	switch (_grade) {
		case 'A':
		case 'A등급':
			return GradeEnum.GRADE_A;
		case 'B':
		case 'B등급':
			return GradeEnum.GRADE_A;
		case 'C':
		case 'C등급':
			return GradeEnum.GRADE_A;
		case '기본등급':
			return GradeEnum.BASIC_GRADE;
		default:
			return grade;
	}
};

export const replaceApplicantStatus = (
	value: string,
	statusListOption: DefaultOptionType[] = []
) => {
	const _value = removeSpace(value);
	return (
		statusListOption.find(
			(option) => removeSpace(option.label as string) === _value
		)?.value || value
	);
};

export const replaceApplicantIssuanceStatus = (
	value: string,
	issuanceStatusListOption: DefaultOptionType[] = []
) => {
	const _value = removeSpace(value);
	return (
		issuanceStatusListOption.find(
			(option) => removeSpace(option.label as string) === _value
		)?.value || value
	);
};

export const replaceVisaCode = (visaCode: string) => {
	switch (visaCode) {
		case ResidenceEnum.C4:
		case ResidenceEnum.E8:
		case ResidenceEnum.C41:
		case ResidenceEnum.C42:
		case ResidenceEnum.C43:
		case ResidenceEnum.C44:
		case ResidenceEnum.E81:
		case ResidenceEnum.E82:
		case ResidenceEnum.E83:
		case ResidenceEnum.E84:
		case ResidenceEnum.E85:
		case ResidenceEnum.E86:
		case ResidenceEnum.E899:
			return visaCode.replace(/_/g, '-');
		default:
			return visaCode;
	}
};

const renderCaroValue = (value: string | number | null) => {
	if (value === 0 || value === null) return CaroEnum.X;
	if (value === 1) return CaroEnum.O;
	return value;
};

const renderDepartureDateDDay = (departureDate: string) => {
	const diffDepartureDate = getDiffExpirationDate(departureDate);
	return diffDepartureDate < 0 ? '' : `D-${diffDepartureDate}`;
};

export const mapCustomFields = (
	parsedData: RowMap,
	options:
		| TUnitSettingStore['optionalFieldApplicantList']
		| TUnitSettingStore['optionalFieldFranchiseList'] = []
): Record<string, any> => {
	const result: Record<string, any> = {};

	options.forEach((field) => {
		const matchedValue = parsedData[field.name];
		if (matchedValue) {
			result[`customField${field.fieldNo}`] = matchedValue;
		}
	});

	return result;
};

export const mapCustomFieldsToFailedList = (
	customFields: string[],
	options:
		| TUnitSettingStore['optionalFieldApplicantList']
		| TUnitSettingStore['optionalFieldFranchiseList'] = [],
	values: Record<string, any>
): Record<string, any> => {
	const result: { [key: string]: number | string } = {
		customField1: '',
		customField2: '',
		customField3: '',
		customField4: '',
		customField5: '',
	};

	customFields.forEach((field) => {
		const matchedValue = options.find((option) => option.name === field);
		if (matchedValue) {
			const fieldKey = `customField${matchedValue.fieldNo}`;
			result[fieldKey] = formatCustomFieldValue(values[fieldKey]);
		}
	});

	const sortedResult: { [key: string]: number | string } = {};

	// Sort based on order of customFields
	customFields.forEach((field) => {
		const matchedValue = options.find((option) => option.name === field);
		if (matchedValue) {
			const fieldKey = `customField${matchedValue.fieldNo}`;
			sortedResult[fieldKey] = result[fieldKey];
		}
	});

	// Add any remaining fields (those not in customFields) to the end of sortedResult
	Object.entries(result).forEach(([key, value]) => {
		if (!sortedResult[key]) {
			sortedResult[key] = value;
		}
	});

	return sortedResult;
};

const getFieldLabel =
	<T>(fields: { value: keyof T; label: string }[]) =>
	(fieldValue: keyof T): string =>
		fields.find((field) => field.value === fieldValue)?.label || '';

export const convertDataApplicantExcelToRequestBody = (
	parsedData: RowMap[],
	option?: {
		districtListOption?: DefaultOptionType[];
		optionalFieldApplicantList?: TUnitSettingStore['optionalFieldApplicantList'];
		statusListOption?: DefaultOptionType[];
	}
) => {
	const getApplicantFieldLabel = getFieldLabel<TUploadExcelApplicantItem>(
		TEMPLATE_APPLICANT_FIELDS
	);

	const set = new Set<string>();
	parsedData.forEach((item) => {
		const newObject: Partial<TUploadExcelApplicantItem> = {
			district: item[getApplicantFieldLabel('district')],
			firstName: item[getApplicantFieldLabel('firstName')],
			lastName: item[getApplicantFieldLabel('lastName')],
			birthDate: item[getApplicantFieldLabel('birthDate')],
			email: item[getApplicantFieldLabel('email')],
			gender: item[getApplicantFieldLabel('gender')],
			height: item[getApplicantFieldLabel('height')],
			weight: item[getApplicantFieldLabel('weight')],
			phone: item[getApplicantFieldLabel('phone')],
			hometownAddr: item[getApplicantFieldLabel('hometownAddr')],
			hometownPhone: item[getApplicantFieldLabel('hometownPhone')],
			nationalCode: item[getApplicantFieldLabel('nationalCode')],
			passportNo: item[getApplicantFieldLabel('passportNo')],
			passportIssueDate: item[getApplicantFieldLabel('passportIssueDate')],
			passportExpireDate: item[getApplicantFieldLabel('passportExpireDate')],
			applicationType: item[getApplicantFieldLabel('applicationType')],
			isReEntry: item[getApplicantFieldLabel('isReEntry')],
			reEntryCount: item[getApplicantFieldLabel('reEntryCount')],
			isPublic: item[getApplicantFieldLabel('isPublic')],
			publicFranchiseName: item[getApplicantFieldLabel('publicFranchiseName')],
			grade: item[getApplicantFieldLabel('grade')],
			status: item[getApplicantFieldLabel('status')],
			visaIssuanceConfirmNo:
				item[getApplicantFieldLabel('visaIssuanceConfirmNo')],
			visaNo: item[getApplicantFieldLabel('visaNo')] || null,
			foreignerNo: item[getApplicantFieldLabel('foreignerNo')] || null,
			entryDate: item[getApplicantFieldLabel('entryDate')],
			visaCode: item[getApplicantFieldLabel('visaCode')],
			visaTime: item[getApplicantFieldLabel('visaTime')],
			extensionPeriod: item[getApplicantFieldLabel('extensionPeriod')],
			midTermDeparture: item[getApplicantFieldLabel('midTermDeparture')],
			changeWorkDate: item[getApplicantFieldLabel('changeWorkDate')],
			unauthorizedDepartureDate:
				item[getApplicantFieldLabel('unauthorizedDepartureDate')],
			departureDateDDay: item[getApplicantFieldLabel('departureDateDDay')],
			departureDate: item[getApplicantFieldLabel('departureDate')],
			memo: item[getApplicantFieldLabel('memo')],
			...mapCustomFields(item, option?.optionalFieldApplicantList),
		};

		set.add(JSON.stringify(newObject));
	});

	return Array.from(set).map((item) => JSON.parse(item));
};

export const convertDataRecommenderExcelToRequestBody = (
	parsedData: RowMap[]
) => {
	const getRecommenderFieldLabel = getFieldLabel<TUploadExcelRecommenderItem>(
		TEMPLATE_RECOMMENDER_FIELDS
	);
	const set = new Set<string>();
	parsedData.forEach((item) => {
		const newObject: Partial<TUploadExcelRecommenderItem> = {
			id: item[getRecommenderFieldLabel('id')],
			type: item[getRecommenderFieldLabel('type')],
			name: item[getRecommenderFieldLabel('name')],
			birthDateStr: item[getRecommenderFieldLabel('birthDateStr')],
			phone: item[getRecommenderFieldLabel('phone')],
			address: item[getRecommenderFieldLabel('address')],
			email: item[getRecommenderFieldLabel('email')],
			engName: item[getRecommenderFieldLabel('engName')],
			providerNational: item[getRecommenderFieldLabel('providerNational')],
			visaCode: item[getRecommenderFieldLabel('visaCode')],
			subType: item[getRecommenderFieldLabel('subType')],
			spouseName: item[getRecommenderFieldLabel('spouseName')],
			spousePhone: item[getRecommenderFieldLabel('spousePhone')],
			recommendedNumberPeople:
				item[getRecommenderFieldLabel('recommendedNumberPeople')],
			memo: item[getRecommenderFieldLabel('memo')],
		};

		set.add(JSON.stringify(newObject));
	});

	return Array.from(set).map((item) => JSON.parse(item));
};

export const convertDataLodgingExcelToRequestBody = (parsedData: RowMap[]) => {
	const getLodgingFieldLabel = getFieldLabel<TUploadExcelLodgingItem>(
		TEMPLATE_LODGING_FIELDS
	);

	const set = new Set<string>();
	parsedData.forEach((item) => {
		const newObject: Partial<TUploadExcelLodgingItem> = {
			...insertObjectIf(item[getLodgingFieldLabel('id')], {
				id: item[getLodgingFieldLabel('id')],
			}),
			district: item[getLodgingFieldLabel('district')],
			address: item[getLodgingFieldLabel('address')],
			ownershipType: item[getLodgingFieldLabel('ownershipType')],
			facilityType: item[getLodgingFieldLabel('facilityType')],
			providedDate: item[getLodgingFieldLabel('providedDate')],
			inspectionDate: item[getLodgingFieldLabel('inspectionDate')],
			providerType: item[getLodgingFieldLabel('providerType')],
			providerName: item[getLodgingFieldLabel('providerName')],
			providerNationalCode: replaceNational(
				item[getLodgingFieldLabel('providerNationalCode')]
			),
			providerPhone: item[getLodgingFieldLabel('providerPhone')],
			providerAddress: item[getLodgingFieldLabel('providerAddress')],
			memo: item[getLodgingFieldLabel('memo')],
			lodgingCost: item[getLodgingFieldLabel('lodgingCost')],
			mealCost: item[getLodgingFieldLabel('mealCost')],
		};

		set.add(JSON.stringify(newObject));
	});

	return Array.from(set).map((item) => JSON.parse(item));
};

export const convertDataAssignmentExcelToRequestBody = (
	parsedData: RowMap[]
) => {
	const getAssignmentFieldLabel = getFieldLabel<TUploadExcelAssignmentItem>(
		TEMPLATE_ASSIGNMENT_FIELDS
	);

	const set = new Set<string>();
	parsedData.forEach((item, index) => {
		const newObject: Partial<TUploadExcelAssignmentItem> = {
			programName: item[getAssignmentFieldLabel('programName')],
			applicantFirstName: item[getAssignmentFieldLabel('applicantFirstName')],
			applicantLastName: item[getAssignmentFieldLabel('applicantLastName')],
			applicantBirthDate: item[getAssignmentFieldLabel('applicantBirthDate')],
			applicantEmail: item[getAssignmentFieldLabel('applicantEmail')],
			applicantMemo: item[getAssignmentFieldLabel('applicantMemo')],
			franchiseName: item[getAssignmentFieldLabel('franchiseName')],
			franchiseEmail: item[getAssignmentFieldLabel('franchiseEmail')],
			startDate: item[getAssignmentFieldLabel('startDate')],
			endDate: item[getAssignmentFieldLabel('endDate')],
			requestApplicant: item[getAssignmentFieldLabel('requestApplicant')],
			memo: item[getAssignmentFieldLabel('memo')],
			no: index + 1,
		};

		set.add(JSON.stringify(newObject));
	});

	return Array.from(set).map((item) => JSON.parse(item));
};

export const convertDataPublicAssignmentExcelToRequestBody = (
	parsedData: RowMap[]
) => {
	const getAssignmentFieldLabel =
		getFieldLabel<TUploadExcelPublicAssignmentItem>(
			TEMPLATE_PUBLIC_ASSIGNMENT_FIELDS
		);

	const set = new Set<string>();
	parsedData.forEach((item, index) => {
		const newObject: Partial<TUploadExcelPublicAssignmentItem> = {
			// programName: item[getAssignmentFieldLabel('programName')],
			employerName: item[getAssignmentFieldLabel('employerName')],
			employerBirthDate: item[getAssignmentFieldLabel('employerBirthDate')],
			workingDate: item[getAssignmentFieldLabel('workingDate')],
			startTime: item[getAssignmentFieldLabel('startTime')],
			endTime: item[getAssignmentFieldLabel('endTime')],
			relaxTime: item[getAssignmentFieldLabel('relaxTime')],
			requestApplicant: item[getAssignmentFieldLabel('requestApplicant')],
			autoAssignment: item[getAssignmentFieldLabel('autoAssignment')],
			numberOfMale: item[getAssignmentFieldLabel('numberOfMale')],
			ageOfMale: item[getAssignmentFieldLabel('ageOfMale')],
			numberOfFemale: item[getAssignmentFieldLabel('numberOfFemale')],
			ageOfFemale: item[getAssignmentFieldLabel('ageOfFemale')],
			applicantFirstName: item[getAssignmentFieldLabel('applicantFirstName')],
			applicantLastName: item[getAssignmentFieldLabel('applicantLastName')],
			applicantBirthDate: item[getAssignmentFieldLabel('applicantBirthDate')],
			memo: item[getAssignmentFieldLabel('memo')],
			no: index + 1,
		};

		set.add(JSON.stringify(newObject));
	});

	return Array.from(set).map((item) => JSON.parse(item));
};

export const convertDataFranchiseExcelToRequestBody = (
	parsedData: RowMap[],
	option?: {
		optionalFieldFranchiseList?: TUnitSettingStore['optionalFieldFranchiseList'];
	}
) => {
	const getFranchiseFieldLabel = getFieldLabel<TUploadExcelFranchiseItem>(
		TEMPLATE_FRANCHISE_FIELDS
	);

	const set = new Set<string>();
	parsedData.forEach((item) => {
		const newObject: Partial<TUploadExcelFranchiseItem> = {
			type: item[getFranchiseFieldLabel('type')],
			typeDetail: item[getFranchiseFieldLabel('typeDetail')],
			district: item[getFranchiseFieldLabel('district')],
			employerName: item[getFranchiseFieldLabel('employerName')],
			email: item[getFranchiseFieldLabel('email')],
			address: item[getFranchiseFieldLabel('address')],
			employerBirthDate: item[getFranchiseFieldLabel('employerBirthDate')],
			employerPhone: item[getFranchiseFieldLabel('employerPhone')],
			name: item[getFranchiseFieldLabel('name')],
			phone: item[getFranchiseFieldLabel('phone')],
			productionType: item[getFranchiseFieldLabel('productionType')],
			productionTypeDetail:
				item[getFranchiseFieldLabel('productionTypeDetail')],
			productionScale: item[getFranchiseFieldLabel('productionScale')],
			isPublic: item[getFranchiseFieldLabel('isPublic')],
			publicFranchiseName: item[getFranchiseFieldLabel('publicFranchiseName')],
			businessRegistNo: item[getFranchiseFieldLabel('businessRegistNo')],
			corporateRegistNo: item[getFranchiseFieldLabel('corporateRegistNo')],
			penaltyPointsAccumulated:
				item[getFranchiseFieldLabel('penaltyPointsAccumulated')],
			isAllocationRestricted:
				item[getFranchiseFieldLabel('isAllocationRestricted')],
			workingTime: item[getFranchiseFieldLabel('workingTime')],
			monthlyWorkingHours: item[getFranchiseFieldLabel('monthlyWorkingHours')],
			dailyBreaksNumber: item[getFranchiseFieldLabel('dailyBreaksNumber')],
			dailyBreaksHour: item[getFranchiseFieldLabel('dailyBreaksHour')],
			dailyBreaksMin: item[getFranchiseFieldLabel('dailyBreaksMin')],
			gradeADailyWage: item[getFranchiseFieldLabel('gradeADailyWage')],
			gradeBDailyWage: item[getFranchiseFieldLabel('gradeBDailyWage')],
			gradeCDailyWage: item[getFranchiseFieldLabel('gradeCDailyWage')],
			defaultGrade: item[getFranchiseFieldLabel('defaultGrade')],
			memo: item[getFranchiseFieldLabel('memo')],
			...mapCustomFields(item, option?.optionalFieldFranchiseList),
		};

		set.add(JSON.stringify(newObject));
	});

	return Array.from(set).map((item) => JSON.parse(item));
};

export const convertDataExcelToRequestBody = ({
	parsedData,
	pageType,
	option = {
		districtListOption: [],
		optionalFieldApplicantList: [],
		statusListOption: [],
	},
}: {
	parsedData: RowMap[];
	pageType: TUploadExcelPage;
	option?: {
		districtListOption?: DefaultOptionType[];
		optionalFieldApplicantList?: TUnitSettingStore['optionalFieldApplicantList'];
		optionalFieldFranchiseList?: TUnitSettingStore['optionalFieldFranchiseList'];
		statusListOption?: DefaultOptionType[];
	};
}) => {
	switch (pageType) {
		case PageTypeEnum.APPLICANT:
			return convertDataApplicantExcelToRequestBody(parsedData, {
				districtListOption: option.districtListOption,
				statusListOption: option.statusListOption,
				optionalFieldApplicantList: option.optionalFieldApplicantList,
			});
		case PageTypeEnum.RECOMMENDER:
			return convertDataRecommenderExcelToRequestBody(parsedData);
		case PageTypeEnum.LODGING:
			return convertDataLodgingExcelToRequestBody(parsedData);
		case PageTypeEnum.ASSIGNMENT:
			return convertDataAssignmentExcelToRequestBody(parsedData);
		case PageTypeEnum.FRANCHISE:
			return convertDataFranchiseExcelToRequestBody(parsedData, {
				optionalFieldFranchiseList: option.optionalFieldFranchiseList,
			});
		case PageTypeEnum.PUBLIC_ASSIGNMENT:
			return convertDataPublicAssignmentExcelToRequestBody(parsedData);
	}
};

export const sortOrderApplicantToExport = (
	applicant: TApplicantUploadFailedDetailStore | IApplicantListItem,
	type: 'failedData' | 'currentData',
	{
		customFields = [],
		options = [],
		t,
	}: {
		customFields?: string[];
		options?: TUnitSettingStore['optionalFieldApplicantList'];
		t: TFunction;
	}
) => {
	const isFailedData = type === 'failedData';
	const prefix = isFailedData ? '' : 'applicant';

	const getField = (field: string): string => {
		const key =
			prefix === ''
				? field
				: `${prefix}${field.charAt(0).toUpperCase() + field.slice(1)}`;

		return (applicant as any)[key] ?? '';
	};

	const result = {
		district: isFailedData ? getField('district') : getField('districtName'),
		firstName: getField('firstName'),
		lastName: getField('lastName'),
		birthDate: formatDateToExport(getField('birthDate')),
		email: getField('email'),
		gender: isFailedData
			? getField('gender')
			: convertGenderCodeToName(getField('gender')),
		height: getField('height'),
		weight: getField('weight'),
		phone: getField('phone'),
		hometownAddr: getField('hometownAddr'),
		hometownPhone: getField('hometownPhone'),
		nationalCode: convertNationCodeToName(getField('nationalCode') as string),
		passportNo: getField('passportNo'),
		passportIssueDate: formatDateToExport(getField('passportIssueDate')),
		passportExpireDate: formatDateToExport(getField('passportExpireDate')),
		applicationType: isFailedData
			? getField('applicationType')
			: t(convertApplicantTypeCodeToName(getField('applicationType')) as any),
		isReEntry: renderCaroValue(getField('isReEntry')),
		reEntryCount: getField('reEntryCount'),
		isPublic: renderCaroValue(getField('isPublic')),
		publicFranchiseName: getField('publicFranchiseName'),
		grade: isFailedData
			? getField('grade')
			: convertGradeCodeToName(getField('grade')),
		status: isFailedData ? getField('status') : getField('statusName'),
		visaIssuanceConfirmNo: getField('visaIssuanceConfirmNo'),
		visaNo: getField('visaNo'),
		foreignerNo: getField('foreignerNo'),
		entryDate: formatDateToExport(getField('entryDate')),
		visaCode: replaceVisaCode(getField('visaCode')),
		visaTime: isFailedData
			? getField('visaTime')
			: VisaTimeLabel[getField('visaTime') as VisaTimeEnum],
		extensionPeriod: isFailedData
			? getField('extensionPeriod')
			: ExtensionStayLabel[getField('extensionPeriod') as ExtensionStayEnum],
		midTermDeparture: getField('midTermDeparture'),
		changeWorkDate: formatDateToExport(getField('changeWorkDate')),
		unauthorizedDepartureDate: formatDateToExport(
			getField('unauthorizedDepartureDate')
		),
		departureDateDDay: isFailedData
			? getField('departureDateDDay')
			: renderDepartureDateDDay(getField('departureDate')),
		departureDate: formatDateToExport(getField('departureDate')),
		memo: getField('memo'),
		...mapCustomFieldsToFailedList(customFields, options, {
			customField1: getField('customField1'),
			customField2: getField('customField2'),
			customField3: getField('customField3'),
			customField4: getField('customField4'),
			customField5: getField('customField5'),
		}),
	};

	return isFailedData
		? {
				...result,
				reasonFailure:
					(applicant as TApplicantUploadFailedDetailStore).reasonFailure || '',
			}
		: result;
};

export const sortOrderRecommenderToExport = (
	recommender:
		| TExportExcelRecommenderResponse[number]
		| TRecommenderUploadFailedDetailStore,
	type: 'failedData' | 'currentData'
) => {
	const result = {
		id: recommender.id,
		type: getRecommenderTypeLabel(recommender?.type || ''),
		name: recommender.name || '',
		birthDate:
			'birthDateStr' in recommender
				? recommender.birthDateStr
				: formatDateToExport(recommender.birthDate) || '',
		phone: recommender.phone || '',
		address: recommender.address || '',
		email: recommender.email || '',
		engName: recommender.engName || '',
		providerNational:
			'providerNationalCode' in recommender
				? convertNationCodeToName(recommender.providerNationalCode)
				: recommender.providerNational || '',
		visaCode:
			'visaCodeDisplay' in recommender
				? recommender.visaCodeDisplay
				: recommender.visaCode || '',
		subType: recommender.subType || '',
		spouseName: recommender.spouseName || '',
		spousePhone: recommender.spousePhone || '',
		recommendedNumberPeople:
			'recommendedNumberPeople' in recommender
				? formatNumber(recommender.recommendedNumberPeople)
				: '',
		memo: recommender.memo || '',
	};

	if (type === 'failedData') {
		return {
			...result,
			reasonFailure:
				'reasonFailure' in recommender ? recommender.reasonFailure : '',
		};
	}

	return result;
};

export const sortOrderLodgingToExport = (
	lodging: TUploadExcelLodgingFailedItemStore | WorkerLodgingListItem,
	type: 'failedData' | 'currentData'
) => {
	const result = {
		id: 'lodgingId' in lodging ? lodging.lodgingId : lodging.id,
		district:
			'district' in lodging ? lodging.district : lodging.lodgingDistrictName,
		address: 'address' in lodging ? lodging.address : lodging.lodgingAddress,
		ownershipType:
			'ownershipType' in lodging
				? lodging.ownershipType
				: convertOwnershipCodeToName(lodging.lodgingOwnershipType),
		facilityType:
			'facilityType' in lodging
				? lodging.facilityType
				: convertInfrastructureCodeToName(lodging.lodgingFacilityType),
		lodgingAssignCnt: formatNumber(lodging.lodgingAssignCnt),
		providedDate:
			'providedDate' in lodging
				? lodging.providedDate
				: formatDateToExport(lodging.lodgingProvidedDate),
		inspectionDate:
			'inspectionDate' in lodging
				? lodging.inspectionDate
				: formatDateToExport(lodging.lodgingInspectionDate),
		lodgingCost:
			'lodgingCost' in lodging
				? lodging.lodgingCost
				: lodging.lodgingLodgingCost,
		mealCost:
			'mealCost' in lodging ? lodging.mealCost : lodging.lodgingMealCost,
		providerType:
			'providerType' in lodging
				? lodging.providerType
				: convertLodgingProviderTypeCodeToName(lodging.lodgingProviderType),
		providerName:
			'providerName' in lodging
				? lodging.providerName
				: lodging.lodgingProviderName,
		providerNationalCode:
			'providerNationalCode' in lodging
				? convertNationCodeToName(lodging.providerNationalCode)
				: convertNationCodeToName(lodging.lodgingProviderNationalCode),
		providerPhone:
			'providerPhone' in lodging
				? lodging.providerPhone
				: lodging.lodgingProviderPhone,
		providerAddress:
			'providerAddress' in lodging
				? lodging.providerAddress
				: lodging.lodgingProviderAddress,
		memo: 'memo' in lodging ? lodging.memo : lodging.lodgingMemo,
	};

	if (type === 'failedData') {
		return {
			...result,
			reasonFailure: 'reasonFailure' in lodging ? lodging.reasonFailure : '',
		};
	}

	return result;
};

export const sortOrderAssignmentToExport = (
	assignment:
		| IWorkerAssignmentUploadAssignmentFailedDetailStore
		| TUploadExcelCurrentAssignmentItem,
	type: 'failedData' | 'currentData'
) => {
	const isFailedData = type === 'failedData';
	const currentAssignment = assignment as TUploadExcelCurrentAssignmentItem;
	const failedAssignment =
		assignment as IWorkerAssignmentUploadAssignmentFailedDetailStore;

	const result = {
		applicantFirstName: assignment?.applicantFirstName || '',
		applicantLastName: assignment?.applicantLastName || '',
		applicantBirthDate: assignment?.applicantBirthDate || '',
		applicantEmail: assignment?.applicantEmail || '',
		applicantMemo: isFailedData
			? failedAssignment?.applicantMemo
			: currentAssignment?.assignmentApplicantMemo || '',
		franchiseName: assignment?.franchiseName || '',
		franchiseEmail: assignment?.franchiseEmail || '',
		startDate: isFailedData
			? failedAssignment?.startDate
			: currentAssignment?.assignmentFranchiseStartDate || '',
		endDate: isFailedData
			? failedAssignment?.endDate
			: currentAssignment?.assignmentFranchiseEndDate || '',
		requestApplicant: isFailedData
			? failedAssignment?.requestApplicant
			: formatNumber(currentAssignment?.assignmentFranchiseRequestApplicant) ||
				'',
		memo: isFailedData
			? failedAssignment?.memo
			: currentAssignment?.assignmentFranchiseMemo || '',
	};

	if (isFailedData) {
		return {
			...result,
			reasonFailure: failedAssignment?.reasonFailure || '',
		};
	}

	return result;
};

export const sortOrderPublicAssignmentToExport = (
	assignment: IWorkerAssignmentUploadPublicAssignmentFailedDetailStore,
	type: 'failedData' | 'currentData'
) => {
	const isFailedData = type === 'failedData';
	const failedAssignment =
		assignment as IWorkerAssignmentUploadPublicAssignmentFailedDetailStore;

	const result = {
		employerName: failedAssignment?.employerName || '',
		employerBirthDate: failedAssignment?.employerBirthDate || '',
		workingDate: failedAssignment?.workingDate || '',
		startTime: failedAssignment?.startTime || '',
		endTime: failedAssignment?.endTime || '',
		relaxTime: failedAssignment?.relaxTime || '',
		requestApplicant: failedAssignment?.requestApplicant || '',
		autoAssignment: failedAssignment?.autoAssignment || '',
		numberOfMale: failedAssignment?.numberOfMale || '',
		ageOfMale: failedAssignment?.ageOfMale || '',
		numberOfFemale: failedAssignment?.numberOfFemale || '',
		ageOfFemale: failedAssignment?.ageOfFemale || '',
		applicantFirstName: failedAssignment?.applicantFirstName || '',
		applicantLastName: failedAssignment?.applicantLastName || '',
		applicantBirthDate: failedAssignment?.applicantBirthDate || '',
		memo: failedAssignment?.memo || '',
	};

	if (isFailedData) {
		return {
			...result,
			reasonFailure: failedAssignment?.reasonFailure || '',
		};
	}

	return result;
};

const showWorkingTime = (startTime: string, endTime: string) => {
	const formattedStartTime = formatDate(startTime, {
		inputFormat: 'HH:mm:ss',
		outputFormat: 'HH:mm',
	});
	const formattedEndTime = formatDate(endTime, {
		inputFormat: 'HH:mm:ss',
		outputFormat: 'HH:mm',
	});
	return !formattedStartTime && !formattedEndTime
		? ''
		: `${formattedStartTime || ''}~${formattedEndTime || ''}`;
};

const showPublicGrade = (value: number | null) => {
	return value === 1 ? '예' : '아니요';
};

const showIsAllocationRestricted = (value: number | null) => {
	return value === 1 ? '해당있음' : '해당없음';
};

const formatNumber = (value: number | string | null | undefined) => {
	if (value === null || value === undefined || value === '') {
		return '';
	}

	const numericValue = Number(value);

	if (isNaN(numericValue)) {
		return value;
	} else return numericValue?.toLocaleString('ko-KR');
};

export const formatCustomFieldValue = (value: string) => {
	// check if has format YYYY-MM-DD
	const dateRegex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
	if (dateRegex.test(value)) {
		const date = dayjs(value, SUPPORT_FORMAT_DATE_LIST);
		// check if date is valid and format is YYYY-MM-DD
		if (date.isValid() && date.format('YYYY-MM-DD') === value) {
			return formatDate(value);
		}
	}

	return value;
};

export const formatDateToExport = (value: string) => {
	// check if value is valid date, format YYYY-MM-DD or YYYY.MM.DD
	const dateRegex = /^\d{4}([-.])(?:0[1-9]|1[0-2])\1(?:0[1-9]|[12]\d|3[01])$/;

	if (dateRegex.test(value)) {
		// split value to year, month, day
		const [year, month, day] = value.split(/[-.]/).map(Number);
		const pad = (n: number) => n.toString().padStart(2, '0');

		// create date object to check valid date
		const date = dayjs(`${year}-${pad(month)}-${pad(day)}`, 'YYYY-MM-DD');
		// check if date is valid and match input
		// this will catch case like 2025-02-29
		if (
			date.isValid() &&
			date.year() === year &&
			date.month() + 1 === month && // dayjs month start from 0
			date.date() === day
		) {
			return formatDate(value);
		}
	}
	return value;
};

export const sortOrderFranchiseToExport = (
	franchise: TFranchiseUploadFailedDetailStore | IFranchiseListItem,
	type: 'failedData' | 'currentData',
	{
		customFields = [],
		options = [],
	}: {
		customFields?: string[];
		options?: TUnitSettingStore['optionalFieldFranchiseList'];
	}
) => {
	const isFailedData = type === 'failedData';
	const currentFranchise = franchise as IFranchiseListItem;
	const failedFranchise = franchise as TFranchiseUploadFailedDetailStore;

	const result = {
		type: isFailedData
			? convertFranchiseTypeCodeToName(failedFranchise.type)
			: convertFranchiseTypeCodeToName(currentFranchise.franchiseType),
		typeDetail: isFailedData
			? convertFranchiseTypeDetailCodeToName(failedFranchise.typeDetail)
			: convertFranchiseTypeDetailCodeToName(
					currentFranchise.franchiseTypeDetail
				),
		district: isFailedData
			? failedFranchise.district
			: currentFranchise.franchiseDistrictName,
		employerName: isFailedData
			? failedFranchise.employerName
			: currentFranchise.franchiseEmployerName,
		email: isFailedData
			? failedFranchise.email
			: currentFranchise.franchiseEmail,
		address: isFailedData
			? failedFranchise.address
			: currentFranchise.franchiseAddress,
		employerBirthDate: isFailedData
			? formatDateToExport(failedFranchise.employerBirthDate)
			: formatDateToExport(currentFranchise.franchiseEmployerBirthDate),
		employerPhone: isFailedData
			? failedFranchise.employerPhone
			: currentFranchise.franchiseEmployerPhone,
		name: isFailedData ? failedFranchise.name : currentFranchise.franchiseName,
		phone: isFailedData
			? failedFranchise.phone
			: currentFranchise.franchisePhone,
		productionType: isFailedData
			? convertProductionTypeCodeToName(failedFranchise.productionType)
			: convertProductionTypeCodeToName(
					currentFranchise.franchiseProductionType
				),
		productionTypeDetail: isFailedData
			? failedFranchise.productionTypeDetail
			: currentFranchise.franchiseProductionTypeDetail,
		productionScale: isFailedData
			? failedFranchise.productionScale
			: formatNumber(currentFranchise.franchiseProductionScale),
		maxManpowerAllowed: isFailedData
			? failedFranchise.maxManpowerAllowed
			: formatNumber(currentFranchise.franchiseMaxManpowerAllowed),
		isPublic: isFailedData
			? failedFranchise.isPublic
			: showPublicGrade(currentFranchise.franchiseIsPublic as number),
		publicFranchiseName: isFailedData
			? failedFranchise.publicFranchiseName
			: currentFranchise.franchisePublicFranchiseName,
		businessRegistNo: isFailedData
			? failedFranchise.businessRegistNo
			: currentFranchise.franchiseBusinessRegistNo,
		corporateRegistNo: isFailedData
			? failedFranchise.corporateRegistNo
			: currentFranchise.franchiseCorporateRegistNo,
		penaltyPointsAccumulated: isFailedData
			? failedFranchise.penaltyPointsAccumulated
			: currentFranchise.franchisePenaltyPointsAccumulated,
		isAllocationRestricted: isFailedData
			? failedFranchise.isAllocationRestricted
			: showIsAllocationRestricted(
					currentFranchise.franchiseIsAllocationRestricted as number
				),
		workingTime: isFailedData
			? failedFranchise.workingTime
			: showWorkingTime(
					currentFranchise?.franchiseWorkStartTime,
					currentFranchise?.franchiseWorkEndTime
				),
		monthlyWorkingHours: isFailedData
			? failedFranchise.monthlyWorkingHours
			: formatNumber(currentFranchise.franchiseMonthlyWorkingHours),
		dailyBreaksNumber: isFailedData
			? failedFranchise.dailyBreaksNumber
			: formatNumber(currentFranchise.franchiseDailyBreaksNumber),
		dailyBreaksHour: isFailedData
			? failedFranchise.dailyBreaksHour
			: currentFranchise.franchiseDailyBreaksHour,
		dailyBreaksMin: isFailedData
			? failedFranchise.dailyBreaksMin
			: currentFranchise.franchiseDailyBreaksMin,
		gradeADailyWage: isFailedData
			? failedFranchise.gradeADailyWage
			: currentFranchise.franchiseGradeAHourlyWage,
		gradeBDailyWage: isFailedData
			? failedFranchise.gradeBDailyWage
			: currentFranchise.franchiseGradeBHourlyWage,
		gradeCDailyWage: isFailedData
			? failedFranchise.gradeCDailyWage
			: currentFranchise.franchiseGradeCHourlyWage,
		defaultGrade: isFailedData
			? convertGradeCodeToName(failedFranchise.defaultGrade)
			: convertGradeCodeToName(currentFranchise.franchiseDefaultGrade),
		memo: isFailedData ? failedFranchise.memo : currentFranchise.franchiseMemo,
		...mapCustomFieldsToFailedList(customFields, options, {
			customField1: isFailedData
				? failedFranchise.customField1
				: currentFranchise.franchiseCustomField1,
			customField2: isFailedData
				? failedFranchise.customField2
				: currentFranchise.franchiseCustomField2,
			customField3: isFailedData
				? failedFranchise.customField3
				: currentFranchise.franchiseCustomField3,
			customField4: isFailedData
				? failedFranchise.customField4
				: currentFranchise.franchiseCustomField4,
			customField5: isFailedData
				? failedFranchise.customField5
				: currentFranchise.franchiseCustomField5,
		}),
	};

	if (isFailedData) {
		return {
			...result,
			reasonFailure: failedFranchise.reasonFailure || '',
		};
	}

	return result;
};

export const setProgramNameToAssignment = (
	worksheet: ExcelJS.Worksheet,
	programName: string,
	isProgramNameNotFound: boolean | undefined
) => {
	const cellProgramName = worksheet?.getCell(
		CELL_PROGRAM_NAME_ASSIGNMENT_HEADER_DATA
	);
	if (cellProgramName) {
		cellProgramName.value = programName;
	}

	if (isProgramNameNotFound) {
		const cellReasonFail = worksheet?.getCell(
			CELL_PROGRAM_NAME_ASSIGNMENT_REASON_FAIL
		);
		if (cellReasonFail) {
			cellReasonFail.value =
				EXCEL_RESULT_MESSAGES.EXCEL_RESULT_ALL_1('프로그램명');
		}
	}
};

export const exportDataToExcel = async ({
	data,
	filename,
	templateName,
	headerRows,
	pageType,
	customFields,
	optionalFieldApplicantList,
	optionalFieldFranchiseList,
	type,
	additionalAssignment,
	t,
}: {
	data: any[];
	filename: string;
	templateName: string;
	headerRows: number[];
	pageType: TUploadExcelPage;
	type: 'failedData' | 'currentData';
	customFields?: string[];
	optionalFieldApplicantList?: TUnitSettingStore['optionalFieldApplicantList'];
	optionalFieldFranchiseList?: TUnitSettingStore['optionalFieldFranchiseList'];
	additionalAssignment?: {
		isProgramNameNotFound: boolean;
	};
	t: TFunction;
}) => {
	try {
		// read file excel from public folder

		const template = await fetch(
			`${process.env.PUBLIC_URL}/template-excel/${templateName}.xlsx`
		);

		if (!template.ok) {
			throw new Error('Failed to fetch the template');
		}

		const arrayBuffer = await template.arrayBuffer();

		const workbook = new ExcelJS.Workbook();
		await workbook.xlsx.load(arrayBuffer);

		const worksheet = workbook.getWorksheet(1);
		const headerRow = Math.max(...headerRows);
		if (worksheet) {
			// active cell to A1
			worksheet.views = [
				{
					state: 'normal',
					activeCell: 'A1',
				},
			];
		}

		//fill header custom field to template
		if (
			pageType === PageTypeEnum.APPLICANT ||
			pageType === PageTypeEnum.FRANCHISE
		) {
			const startColumnCustomField =
				pageType === PageTypeEnum.APPLICANT
					? columnToNumber(COLUMN_START_CUSTOM_FIELD_APPLICANT)
					: columnToNumber(COLUMN_START_CUSTOM_FIELD_FRANCHISE);

			customFields?.forEach((field, index) => {
				const row = worksheet?.getRow(headerRow);
				if (row) {
					const cell = row.getCell(startColumnCustomField + index);
					if (cell) {
						cell.value = field;
					}
				}
			});
		}

		//fill data
		data.forEach((item, rowIndex) => {
			const excelRow = worksheet?.getRow(rowIndex + headerRow + 1); // Adjust for the correct starting row
			if (!excelRow) return;
			// excelRow.getCell(2).value = rowIndex + 1; // Set the row number or ID

			switch (pageType) {
				case PageTypeEnum.APPLICANT:
					Object.entries(
						sortOrderApplicantToExport(item, type, {
							customFields,
							options: optionalFieldApplicantList || [],
							t,
						})
					).forEach(([key, value], colIndex) => {
						const cell = excelRow.getCell(colIndex + 2);
						if (cell) {
							cell.value = value;
							applyCellStyle(cell);
						}
						excelRow.getCell(colIndex + 2).value = value; // Start filling from the second column
					});
					break;
				case PageTypeEnum.RECOMMENDER:
					Object.entries(sortOrderRecommenderToExport(item, type)).forEach(
						([key, value], colIndex) => {
							const cell = excelRow.getCell(colIndex + 2);
							if (cell) {
								cell.value = value as ExcelJS.CellValue;
								applyCellStyle(cell);
							}
							excelRow.getCell(colIndex + 2).value = value as ExcelJS.CellValue; // Start filling from the second column
						}
					);
					break;
				case PageTypeEnum.LODGING:
					Object.entries(sortOrderLodgingToExport(item, type)).forEach(
						([key, value], colIndex) => {
							const cell = excelRow.getCell(colIndex + 2);
							if (cell) {
								cell.value = value;
								applyCellStyle(cell);
							}
							excelRow.getCell(colIndex + 2).value = value; // Start filling from the second column
						}
					);
					break;
				case PageTypeEnum.ASSIGNMENT:
					Object.entries(sortOrderAssignmentToExport(item, type) || {}).forEach(
						([key, value], colIndex) => {
							const cell = excelRow.getCell(colIndex + 2);
							if (cell) {
								cell.value = value as ExcelJS.CellValue;
								applyCellStyle(cell);
							}
							excelRow.getCell(colIndex + 2).value = value; // Start filling from the second column
						}
					);
					setProgramNameToAssignment(
						worksheet!,
						data[0]?.programName,
						additionalAssignment?.isProgramNameNotFound
					);
					break;
				case PageTypeEnum.FRANCHISE:
					Object.entries(
						sortOrderFranchiseToExport(item, type, {
							customFields,
							options: optionalFieldFranchiseList || [],
						})
					).forEach(([key, value], colIndex) => {
						const cell = excelRow.getCell(colIndex + 2);
						if (cell) {
							cell.value = value;
							applyCellStyle(cell);
						}
						excelRow.getCell(colIndex + 2).value = value; // Start filling from the second column
					});
					break;
				case PageTypeEnum.PUBLIC_ASSIGNMENT:
					Object.entries(
						sortOrderPublicAssignmentToExport(item, type) || {}
					).forEach(([key, value], colIndex) => {
						const cell = excelRow.getCell(colIndex + 2);
						if (cell) {
							cell.value = value as ExcelJS.CellValue;
							applyCellStyle(cell);
						}
						excelRow.getCell(colIndex + 2).value = value; // Start filling from the second column
					});
					// setProgramNameToAssignment(
					// 	worksheet!,
					// 	data[0]?.programName,
					// 	additionalAssignment?.isProgramNameNotFound
					// );
					break;
				default:
					break;
			}

			excelRow.commit(); // Commit the row to apply the changes
		});

		// Save the filled template
		const bufferOutput = await workbook.xlsx.writeBuffer();
		const blob = new Blob([bufferOutput], {
			type: FILE_TYPE_EXPORT_EXCEL,
		});

		saveAs(blob, `${filename}.xlsx`);
	} catch (error) {
		console.error(error);
	}
};

export const renderMessageAfterUploadExcel = ({
	successCount,
	failCount,
	pageType,
}: {
	successCount: number;
	failCount: number;
	pageType: TUploadExcelPage;
}) => {
	switch (pageType) {
		case PageTypeEnum.APPLICANT:
			return `${successCount}개의 근로자 등록이 성공하고, ${failCount}개의 근로자 등록이 실패했습니다.`;
		case PageTypeEnum.RECOMMENDER:
			return `${successCount}개의 추천인 등록이 성공하고, ${failCount}개의 추천인 등록이 실패했습니다.`;
		default:
			return '';
	}
};
